import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message } from 'antd'
import {
  extractErrorMessage,
  extractErrorStatus,
} from 'routes/AbpUploadDashboard/utils/extract-error-message'
import {
  cancelCompareRun,
  getActiveCompareRun,
  getCompareRun,
  type CompareRun,
  type CompareRunSource,
  type StartCompareRunResponse,
} from '../api/compare-run-api'

const POLL_INTERVAL_MS = 2_000
const MAX_POLL_FAILURES = 3
// Never leave the user on the spinner forever, whatever the backend does.
const MAX_POLL_DURATION_MS = 30 * 60 * 1000

/**
 * starting: the tab's start step is in flight (e.g. uploading the CSV) — no
 *           run exists yet, so it can't be cancelled.
 * queued:   the run exists but its job hasn't reported progress (it waits
 *           behind another compare, one runs at a time).
 * running:  the job is reporting progress.
 */
export type CompareRunPhase = 'idle' | 'starting' | 'queued' | 'running'

// Backend `message`, else the Error's own message (e.g. a failed S3 upload).
function startErrorMessage(err: unknown): string {
  return extractErrorMessage(
    err,
    err instanceof Error && err.message
      ? err.message
      : 'Could not start the compare. Please try again.'
  )
}

/**
 * The run lifecycle both compare tabs share: start (each tab supplies its own
 * start step), poll GET runs/:id to completion, resume the caller's own
 * in-progress run on mount (e.g. after a refresh), cancel.
 */
export function useCompareRun({
  source,
  onCompleted,
}: {
  source: CompareRunSource
  onCompleted: (run: CompareRun) => void
}) {
  const [phase, setPhase] = useState<CompareRunPhase>('idle')
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** The FAILED run behind `error`, when the failure came from the job. */
  const [failedRun, setFailedRun] = useState<CompareRun | null>(null)

  const runIdRef = useRef<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const failuresRef = useRef(0)
  const startedAtRef = useRef(0)
  const unmountedRef = useRef(false)
  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  const stop = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
    runIdRef.current = null
    setPhase('idle')
    setProgress(0)
    setStage(null)
  }, [])

  const fail = useCallback(
    (message: string, run: CompareRun | null) => {
      setError(message)
      setFailedRun(run)
      stop()
    },
    [stop]
  )

  const poll = useCallback(
    (runId: string) => {
      timeoutRef.current = setTimeout(() => {
        void (async () => {
          if (unmountedRef.current || runIdRef.current !== runId) return
          if (Date.now() - startedAtRef.current > MAX_POLL_DURATION_MS) {
            fail(
              'This is taking longer than expected. The compare may still be running — refresh the page in a few minutes.',
              null
            )
            return
          }
          try {
            const run = await getCompareRun(runId)
            if (unmountedRef.current || runIdRef.current !== runId) return
            failuresRef.current = 0

            if (run.status === 'COMPLETED') {
              // A finished run supersedes any leftover start error (e.g. a
              // start refused while this resumed run was in flight).
              setError(null)
              setFailedRun(null)
              stop()
              onCompletedRef.current(run)
              return
            }
            if (run.status === 'FAILED') {
              fail(run.errorMessage ?? 'Compare failed. Please try again.', run)
              return
            }
            setPhase(run.progress > 0 || run.stage ? 'running' : 'queued')
            setProgress(run.progress)
            setStage(run.stage)
            poll(runId)
          } catch (err) {
            if (unmountedRef.current || runIdRef.current !== runId) return
            // Cancelled runs are deleted, so a 404 just means "gone".
            if (extractErrorStatus(err) === 404) {
              stop()
              return
            }
            console.error('Failed to check compare status', err)
            failuresRef.current += 1
            if (failuresRef.current < MAX_POLL_FAILURES) {
              poll(runId)
              return
            }
            fail('Could not check compare status. Please try again.', null)
          }
        })()
      }, POLL_INTERVAL_MS)
    },
    [fail, stop]
  )

  const track = useCallback(
    (runId: string) => {
      runIdRef.current = runId
      failuresRef.current = 0
      startedAtRef.current = Date.now()
      setPhase('queued')
      setProgress(0)
      setStage(null)
      poll(runId)
    },
    [poll]
  )

  useEffect(() => {
    unmountedRef.current = false
    void (async () => {
      try {
        const { runId } = await getActiveCompareRun(source)
        if (runId && !unmountedRef.current && !runIdRef.current) track(runId)
      } catch (err) {
        console.error('Failed to check for an active compare', err)
      }
    })()
    return () => {
      unmountedRef.current = true
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [source, track])

  /**
   * Runs the tab's start step, then follows the run. A start step that
   * throws shows its message (backend `message`, else the Error's message).
   */
  const start = useCallback(
    async (startRun: () => Promise<StartCompareRunResponse>) => {
      setError(null)
      setFailedRun(null)
      setPhase('starting')
      try {
        const { runId, status } = await startRun()
        if (unmountedRef.current) return
        if (status === 'COMPLETED') {
          // Saved result (an Ideon pair compared before) — open it directly.
          const completed = await getCompareRun(runId)
          if (unmountedRef.current) return
          setPhase('idle')
          onCompletedRef.current(completed)
          return
        }
        track(runId)
      } catch (err) {
        console.error('Failed to start compare', err)
        // The mount check may have resumed an earlier run while this start
        // was in flight — show the error but keep following that run.
        if (runIdRef.current) {
          setError(startErrorMessage(err))
          return
        }
        fail(startErrorMessage(err), null)
      }
    },
    [fail, track]
  )

  const cancel = useCallback(async () => {
    const runId = runIdRef.current
    if (!runId) return
    try {
      await cancelCompareRun(runId)
      stop()
    } catch (err) {
      // 404: nothing of ours to cancel — a run someone else started (joined
      // in progress) or one that just ended. Either way, stop following it.
      if (extractErrorStatus(err) === 404) {
        stop()
        const current = await getCompareRun(runId).catch(() => null)
        if (current?.status === 'RUNNING') {
          void message.info(
            'This compare was started by another user and is still running.'
          )
        }
        return
      }
      // The run is still going; don't show it as failed.
      console.error('Failed to cancel compare', err)
      void message.error('Could not cancel the compare. Please try again.')
    }
  }, [stop])

  const clearError = useCallback(() => {
    setError(null)
    setFailedRun(null)
  }, [])

  return useMemo(
    () => ({
      phase,
      isRunning: phase !== 'idle',
      /** False while starting — there's no run to cancel yet. */
      canCancel: phase === 'queued' || phase === 'running',
      progress,
      stage,
      error,
      failedRun,
      clearError,
      start,
      cancel,
    }),
    [phase, progress, stage, error, failedRun, clearError, start, cancel]
  )
}
